import { useAuth } from '../../context/AuthProvider';
import { useCallback } from 'react';

const REST_API_URL = process.env.REACT_APP_REST_API_URL;
const API_LOGIN = process.env.REACT_APP_API_LOGIN;
const API_PASSWORD = process.env.REACT_APP_API_PASSWORD;

interface RestClientOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  useAuth?: boolean;
}

export const useRestClient = () => {
  const { keycloakInstance } = useAuth();

  const callRest = useCallback(async <T = unknown>(
    endpoint: string,
    options: RestClientOptions = {}
  ): Promise<T> => {
    const { method = 'POST', body, headers: customHeaders = {}, useAuth = false } = options;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    if (useAuth) {
      if (!API_LOGIN || !API_PASSWORD) {
        throw new Error('REST API credentials not configured');
      }
      const credentials = window.btoa(`${API_LOGIN}:${API_PASSWORD}`);
      headers['Authorization'] = `Basic ${credentials}`;
    } else {
      const token = keycloakInstance?.token;
      if (!token) {
        throw new Error('Token not available');
      }
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${REST_API_URL}${endpoint}`;

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        if (!response.ok) {
          throw new Error(`HTTP Error ${response.status}: ${response.statusText}`);
        }
        throw new Error('Invalid server response');
      }

      if (responseData.name === "WEB_SERVICE_EXCEPTION") {
        throw new Error(responseData.message || 'Backend server error');
      }

      const errorCode = responseData.errorCode;
      if (errorCode !== undefined && errorCode !== null) {
        const errorCodeStr = String(errorCode);
        if (errorCodeStr === "000" || (errorCodeStr !== "0" && errorCode !== 0)) {
          const errorMessage = responseData.message || responseData.infos || `Error - Code: ${errorCode}`;
          throw new Error(errorMessage);
        }
      }

      if (!response.ok) {
        const errorMessage = responseData?.message || responseData?.error || `HTTP Error: ${response.status}`;
        throw new Error(errorMessage);
      }

      const responseCode = responseData.responseCode;
      if (responseCode !== undefined && responseCode !== null) {
        const responseCodeStr = String(responseCode);
        const message = responseData.infos || responseData.message || '';
        
        const isSuccessCode = responseCodeStr === "000" || responseCodeStr === "0" || responseCode === 0;
        const hasSuccessMessage = message.toLowerCase().match(/success|processed|successfully|traité|réussi/i);
        
        if (!isSuccessCode && !hasSuccessMessage) {
          throw new Error(message || `Business error - Code: ${responseCode}`);
        }
      }

      return responseData as T;
    } catch (error) {
      console.error('REST API Error:', error);
      throw error instanceof Error ? error : new Error('REST call error');
    }
  }, [keycloakInstance]);

  return callRest;
};
